import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  Program,
  ProgramTemplate,
  ProgramGenerationRequest,
  MuscleActivationData,
  Exercise,
  ProgramCalendarEvent
} from '@/types'
// Mock data removed to prevent UUID contamination

interface ProgramStore {
  // Current program being worked on
  currentProgram: Program | null
  isGenerating: boolean
  generationProgress: number
  
  // Programs list and management
  programs: Program[]
  filteredPrograms: Program[]
  programsLoading: boolean
  programFilters: {
    status?: Program['status']
    clientId?: string
    search?: string
  }
  
  // Templates
  templates: ProgramTemplate[]
  templatesLoading: boolean
  
  // Exercise library and muscle data
  exerciseLibrary: Exercise[]
  muscleActivations: MuscleActivationData[]
  exerciseFilters: {
    muscleGroup?: string
    equipment?: string
    category?: string
    search?: string
  }
  
  // Calendar and scheduling
  calendarEvents: ProgramCalendarEvent[]
  selectedDate: Date
  calendarView: 'month' | 'week' | 'day'
  
  // Workout assignments for day assignment process
  workoutAssignments: Array<{
    workoutId: string
    assignedDate: Date
    programId: string
  }>
  currentAssignmentProgramId: string | null
  
  // UI State
  selectedProgramId: string | null
  isCreateModalOpen: boolean
  isEditModalOpen: boolean
  isTemplateModalOpen: boolean
  draggedExercise: Exercise | null
  
  // Actions - Program Management
  setCurrentProgram: (program: Program | null) => void
  setPrograms: (programs: Program[]) => void
  addProgram: (program: Program) => void
  updateProgram: (id: string, updates: Partial<Program>) => void
  removeProgram: (id: string) => void
  
  // Actions - Generation
  setIsGenerating: (generating: boolean) => void
  setGenerationProgress: (progress: number) => void
  
  // Actions - Filtering
  setProgramFilters: (filters: Partial<ProgramStore['programFilters']>) => void
  setExerciseFilters: (filters: Partial<ProgramStore['exerciseFilters']>) => void
  filterPrograms: () => void
  
  // Actions - Templates
  setTemplates: (templates: ProgramTemplate[]) => void
  addTemplate: (template: ProgramTemplate) => void
  
  // Actions - Exercise Library
  setExerciseLibrary: (exercises: Exercise[]) => void
  setMuscleActivations: (activations: MuscleActivationData[]) => void
  
  // Actions - Calendar
  setCalendarEvents: (events: ProgramCalendarEvent[]) => void
  addCalendarEvent: (event: ProgramCalendarEvent) => void
  updateCalendarEvent: (id: string, updates: Partial<ProgramCalendarEvent>) => void
  removeCalendarEvent: (id: string) => void
  setSelectedDate: (date: Date) => void
  setCalendarView: (view: 'month' | 'week' | 'day') => void
  
  // Actions - Workout Assignments
  setWorkoutAssignments: (assignments: Array<{workoutId: string, assignedDate: Date, programId: string}>) => void
  addWorkoutAssignment: (assignment: {workoutId: string, assignedDate: Date, programId: string}) => void
  removeWorkoutAssignment: (workoutId: string) => void
  clearWorkoutAssignments: (programId?: string) => void
  setCurrentAssignmentProgramId: (programId: string | null) => void
  
  // Actions - UI State
  setSelectedProgramId: (id: string | null) => void
  setIsCreateModalOpen: (open: boolean) => void
  setIsEditModalOpen: (open: boolean) => void
  setIsTemplateModalOpen: (open: boolean) => void
  setDraggedExercise: (exercise: Exercise | null) => void
  
  // Actions - Loading States
  setProgramsLoading: (loading: boolean) => void
  setTemplatesLoading: (loading: boolean) => void
  
  // Complex Actions
  generateProgramFromRequest: (request: ProgramGenerationRequest) => Promise<void>
  duplicateProgram: (programId: string, newName?: string) => void
  resetStore: () => void
}

const initialState = {
  currentProgram: null,
  isGenerating: false,
  generationProgress: 0,
  programs: [],
  filteredPrograms: [],
  programsLoading: false,
  programFilters: {},
  templates: [],
  templatesLoading: false,
  exerciseLibrary: [],
  muscleActivations: [],
  exerciseFilters: {},
  calendarEvents: [], // No mock data to prevent UUID contamination
  selectedDate: new Date(),
  calendarView: 'month' as const,
  workoutAssignments: [],
  currentAssignmentProgramId: null,
  selectedProgramId: null,
  isCreateModalOpen: false,
  isEditModalOpen: false,
  isTemplateModalOpen: false,
  draggedExercise: null,
}

export const useProgramStore = create<ProgramStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Program Management Actions
      setCurrentProgram: (program) => set({ currentProgram: program }),
      
      setPrograms: (programs) => {
        set({ programs })
        get().filterPrograms()
      },
      
      addProgram: (program) => {
        const programs = [...get().programs, program]
        set({ programs })
        get().filterPrograms()
      },
      
      updateProgram: (id, updates) => {
        const programs = get().programs.map(p => 
          p.id === id ? { ...p, ...updates } : p
        )
        set({ programs })
        get().filterPrograms()
        
        // Update current program if it's the one being updated
        const current = get().currentProgram
        if (current && current.id === id) {
          set({ currentProgram: { ...current, ...updates } })
        }
      },
      
      removeProgram: (id) => {
        const programs = get().programs.filter(p => p.id !== id)
        set({ programs })
        get().filterPrograms()
        
        // Clear current program if it's the one being removed
        if (get().currentProgram?.id === id) {
          set({ currentProgram: null })
        }
      },

      // Generation Actions
      setIsGenerating: (generating) => set({ isGenerating: generating }),
      setGenerationProgress: (progress) => set({ generationProgress: progress }),

      // Filtering Actions
      setProgramFilters: (filters) => {
        const newFilters = { ...get().programFilters, ...filters }
        set({ programFilters: newFilters })
        get().filterPrograms()
      },
      
      setExerciseFilters: (filters) => {
        set({ exerciseFilters: { ...get().exerciseFilters, ...filters } })
      },
      
      filterPrograms: () => {
        const { programs, programFilters } = get()
        let filtered = [...programs]
        
        if (programFilters.status) {
          filtered = filtered.filter(p => p.status === programFilters.status)
        }
        
        if (programFilters.clientId) {
          filtered = filtered.filter(p => p.clientId === programFilters.clientId)
        }
        
        if (programFilters.search) {
          const search = programFilters.search.toLowerCase()
          filtered = filtered.filter(p => 
            p.name.toLowerCase().includes(search) ||
            p.description?.toLowerCase().includes(search)
          )
        }
        
        set({ filteredPrograms: filtered })
      },

      // Template Actions
      setTemplates: (templates) => set({ templates }),
      addTemplate: (template) => {
        set({ templates: [...get().templates, template] })
      },

      // Exercise Library Actions
      setExerciseLibrary: (exercises) => set({ exerciseLibrary: exercises }),
      setMuscleActivations: (activations) => set({ muscleActivations: activations }),

      // Calendar Actions
      setCalendarEvents: (events) => set({ calendarEvents: events }),
      
      addCalendarEvent: (event) => {
        set({ calendarEvents: [...get().calendarEvents, event] })
      },
      
      updateCalendarEvent: (id, updates) => {
        const events = get().calendarEvents.map(e => 
          e.id === id ? { ...e, ...updates } : e
        )
        set({ calendarEvents: events })
      },
      
      removeCalendarEvent: (id) => {
        const events = get().calendarEvents.filter(e => e.id !== id)
        set({ calendarEvents: events })
      },
      
      setSelectedDate: (date) => set({ selectedDate: date }),
      setCalendarView: (view) => set({ calendarView: view }),

      // Workout Assignment Actions
      setWorkoutAssignments: (assignments) => set({ workoutAssignments: assignments }),
      
      addWorkoutAssignment: (assignment) => {
        const current = get().workoutAssignments
        // Remove any existing assignment for this workout
        const filtered = current.filter(a => a.workoutId !== assignment.workoutId)
        set({ workoutAssignments: [...filtered, assignment] })
      },
      
      removeWorkoutAssignment: (workoutId) => {
        const current = get().workoutAssignments
        set({ workoutAssignments: current.filter(a => a.workoutId !== workoutId) })
      },
      
      clearWorkoutAssignments: (programId) => {
        if (programId) {
          const current = get().workoutAssignments
          set({ workoutAssignments: current.filter(a => a.programId !== programId) })
        } else {
          set({ workoutAssignments: [] })
        }
      },
      
      setCurrentAssignmentProgramId: (programId) => set({ currentAssignmentProgramId: programId }),

      // UI State Actions
      setSelectedProgramId: (id) => set({ selectedProgramId: id }),
      setIsCreateModalOpen: (open) => set({ isCreateModalOpen: open }),
      setIsEditModalOpen: (open) => set({ isEditModalOpen: open }),
      setIsTemplateModalOpen: (open) => set({ isTemplateModalOpen: open }),
      setDraggedExercise: (exercise) => set({ draggedExercise: exercise }),

      // Loading State Actions
      setProgramsLoading: (loading) => set({ programsLoading: loading }),
      setTemplatesLoading: (loading) => set({ templatesLoading: loading }),

      // Complex Actions
      generateProgramFromRequest: async (_request) => {
        set({ isGenerating: true, generationProgress: 0 })
        
        try {
          // Simulate progress updates
          const progressInterval = setInterval(() => {
            const current = get().generationProgress
            if (current < 90) {
              set({ generationProgress: current + 10 })
            }
          }, 500)
          
          // TODO: Call actual API
          // const generatedProgram = await ProgramService.generateProgram(request)
          
          // Clear interval and complete
          clearInterval(progressInterval)
          set({ generationProgress: 100 })
          
          setTimeout(() => {
            set({ isGenerating: false, generationProgress: 0 })
          }, 1000)
          
        } catch (error) {
          console.error('Program generation failed:', error)
          set({ isGenerating: false, generationProgress: 0 })
          throw error
        }
      },
      
      duplicateProgram: (programId, newName) => {
        const program = get().programs.find(p => p.id === programId)
        if (program) {
          const duplicated: Program = {
            ...program,
            id: `${program.id}-copy-${Date.now()}`,
            name: newName || `${program.name} (Copy)`,
            status: 'draft',
            createdAt: new Date(),
            updatedAt: new Date()
          }
          get().addProgram(duplicated)
        }
      },
      
      resetStore: () => set(initialState)
    }),
    {
      name: 'program-store'
    }
  )
)